'use client';

import type { DeviceType, ProductUnitItem } from '@weberus/shared-types';
import { useMap, APIProvider, Map, AdvancedMarker, InfoWindow, useAdvancedMarkerRef } from '@vis.gl/react-google-maps';
import { cn, TopNavigation } from '@yanolja-rnd/nol-design-system';
import { IcClose } from '@yanolja-rnd/nol-icons';
import { useEffect, useState, useMemo } from 'react';
import { yaNative } from '@weberus/ui/utils/ya-native';
import { useGlobalMapQuery } from '@/app/(pages)/results/api/queries';
import { Failover } from '@/app/_components/common/empty-state';
import DotLoader from '@/app/_components/common/dot-loader';
import { ProductUnitCard } from '@/app/_components/feature/product-unit';
import { convertToProductUnitProps } from '@/app/_components/feature/product-unit/converter';

// 공통 클래스
const baseClass =
  'custom-marker group w-fit text-nowrap rounded-full px-10 py-7 relative flex-shrink-0 text-center border-[1px]';
const basePointClass = 'absolute w-8 h-8 rotate-45 -bottom-5 left-1/2 transform -translate-x-1/2';

// 상태별 마커 클래스
const markerClass = {
  // disabled: `${baseClass} text-text-neutral-sub !typography-body-12-regular border-line-neutral-weak3 bg-static-white hover-never:bg-interaction-neutral-presshover-weak1 hover-never:border-interaction-neutral-disable-main`,
  default: `${baseClass} text-text-neutral-main typography-body-12-bold border-line-neutral-weak3 bg-static-white hover-never:bg-interaction-neutral-presshover-weak1 hover-never:border-interaction-neutral-disable-main`,
  selected: `${baseClass} text-static-white typography-body-12-bold border-static-black bg-static-black`,
};

// 마커 아래 포인트 클래스
const pointClass = {
  // disabled: `${basePointClass} bg-static-white border border-[#dadbdf] border-t-0 border-l-0`,
  default: `${basePointClass} bg-static-white border border-[#dadbdf] border-t-0 border-l-0 group-hover:bg-interaction-neutral-presshover-weak1 group-hover:border-interaction-neutral-disable-main`,
  selected: `${basePointClass} bg-static-black border border-static-black border-t-0 border-l-0`,
};

interface MarkerWithInfoWindowProps {
  data: ProductUnitItem;
  layout: 'bottom' | 'overlay';
  selected: boolean;
  onClick?: () => void;
  onImpressionPlace?: () => void;
  onClickPlace?: () => void;
}

const MarkerWithInfoWindow = ({
  data,
  layout = 'overlay',
  selected = false,
  onClick,
  onImpressionPlace,
  onClickPlace,
}: MarkerWithInfoWindowProps) => {
  const map = useMap();
  const [markerRef, marker] = useAdvancedMarkerRef();
  const position = useMemo((): { latitude: number; longitude: number } => data.marker!.position, [data]);

  const handleClick = (event: google.maps.MapMouseEvent) => {
    event.stop();
    onClick?.();

    if (map) {
      map.panTo({ lat: position.latitude, lng: position.longitude });
    }
  };

  const handleCardImpression = () => {
    onImpressionPlace?.();
  };

  const handleCardClick = () => {
    onClickPlace?.();
    if (yaNative.isEnabled) {
      yaNative.deepLink({ link: data?.data?.action.app });
      return;
    }
    window.open(data?.data?.action.web);
  };

  return (
    <>
      <AdvancedMarker
        ref={markerRef}
        position={{ lat: position.latitude ?? 0, lng: position.longitude ?? 0 }}
        onClick={(e) => handleClick(e)}>
        <div className={selected ? markerClass.selected : markerClass.default}>
          {data?.marker?.title}
          <div className={selected ? pointClass.selected : pointClass.default} />
        </div>
      </AdvancedMarker>
      {selected && layout === 'overlay' && (
        <InfoWindow
          anchor={marker}
          ariaLabel={data?.data?.title}
          minWidth={328}
          maxWidth={328}
          headerDisabled
          onClose={onClick}>
          <ProductUnitCard
            url={data?.data?.action?.web}
            onItemClick={handleCardClick}
            onImpression={handleCardImpression}
            {...convertToProductUnitProps(data)}
          />
        </InfoWindow>
      )}
      {selected && layout === 'bottom' && (
        <div className="fixed bottom-16 left-0 right-0 z-20 flex justify-center pb-[env(safe-area-inset-bottom)]">
          <div className="flex w-full justify-center px-16">
            <ProductUnitCard
              url={data?.data?.action?.web}
              onItemClick={handleCardClick}
              onImpression={handleCardImpression}
              {...convertToProductUnitProps(data)}
            />
          </div>
        </div>
      )}
    </>
  );
};

export interface GlobalMapLayoutProps {
  deviceType?: DeviceType;
  onClose?: () => void;
  onImpression?: () => void;
  onImpressionPlace?: (place: ProductUnitItem) => void;
  onClickPlace?: (place: ProductUnitItem) => void;
}

export const GlobalMapLayout = ({
  deviceType,
  onClose,
  onImpression,
  onImpressionPlace,
  onClickPlace,
}: GlobalMapLayoutProps) => {
  const { placeList, isError, refetch, isDataLoading } = useGlobalMapQuery();
  const [bounds, setBounds] = useState<google.maps.LatLngBoundsLiteral>();
  const [mapLoadError, setMapLoadError] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();

  useEffect(() => {
    if (placeList.length === 0) {
      return;
    }

    const positions = placeList
      .filter((item) => item.marker?.position)
      .map((item) => ({
        lat: item.marker!.position.latitude,
        lng: item.marker!.position.longitude,
      }));

    const bounds = positions.reduce(
      (acc, { lat, lng }) => {
        return {
          north: Math.max(acc.north, lat),
          south: Math.min(acc.south, lat),
          east: Math.max(acc.east, lng),
          west: Math.min(acc.west, lng),
        };
      },
      {
        north: -Infinity,
        south: Infinity,
        east: -Infinity,
        west: Infinity,
      },
    );

    setBounds(bounds);
    onImpression?.();
  }, []);

  const handleClickMap = () => {
    setSelectedIndex(undefined);
  };

  const handleClickMarker = (index: number) => {
    const place = placeList[index];
    if (!place || place?.marker?.disabled) return;

    setSelectedIndex(index === selectedIndex ? undefined : index);
  };

  return (
    <section className="flex h-screen w-screen flex-col items-center overflow-y-visible bg-static-white">
      <header
        className={cn(
          'sticky top-0 z-20 flex w-full justify-center border-b-line-neutral-weak2',
          deviceType === 'etc' ? 'border-b-[1px]' : 'shadow-1',
        )}>
        <div className="w-full max-w-legacy-pc-size">
          <TopNavigation
            deviceType={deviceType}
            title="지도"
            pcType="pcLegacySize"
            leftButton={{
              'aria-label': 'Close',
              icon: <IcClose />,
              onClick: () => onClose?.(),
            }}
          />
        </div>
      </header>
      <div className="relative h-full w-full max-w-legacy-pc-size">
        {(isError || mapLoadError) && (
          <div className="flex w-full items-center justify-center bg-static-white">
            <Failover refetch={refetch} />
          </div>
        )}
        <APIProvider
          apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY}
          libraries={['marker']}
          onError={() => setMapLoadError(true)}>
          <Map
            mapId={process.env.NEXT_PUBLIC_GOOGLE_MAP_ID}
            defaultZoom={13}
            defaultCenter={{ lat: 37.56657, lng: 126.97817 }}
            mapTypeControl={false}
            streetViewControl={false}
            fullscreenControl={false}
            zoomControl={false}
            clickableIcons={false}
            gestureHandling="greedy"
            defaultBounds={bounds}
            minZoom={5}
            maxZoom={16}
            disableDefaultUI
            onClick={handleClickMap}>
            {placeList?.map((item, index) => (
              <MarkerWithInfoWindow
                key={index}
                data={item}
                layout={deviceType === 'etc' ? 'overlay' : 'bottom'}
                selected={selectedIndex === index}
                onImpressionPlace={() => onImpressionPlace?.(item)}
                onClickPlace={() => onClickPlace?.(item)}
                onClick={() => handleClickMarker(index)}
              />
            ))}
          </Map>
        </APIProvider>
        {isDataLoading && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <DotLoader />
          </div>
        )}
      </div>
    </section>
  );
};
